import { Fragment } from 'react'
import { rotationGuide, type FindingGroup } from '@/features/state/lib/findingView'
import { DetailCell } from '@/features/state/components/DetailCell'
import { OccurrenceLine } from '@/features/state/components/OccurrenceLine'
import { SeverityBadge } from '@/shared/components/Badge'

export function GroupedFindingRow({ group, expanded, onToggle }: {
  group: FindingGroup
  expanded: boolean
  onToggle: () => void
}) {
  const { view, occurrences } = group
  const extra = occurrences.length - 1
  // The guidance trufflehog already collects and the UI has never shown. `remediation` is
  // hardcoded null by every state tool today, so it renders nothing - it is here so a tool that
  // starts populating it is surfaced without another change.
  const guide = occurrences.map(rotationGuide).find((g): g is string => !!g) ?? null
  const remediation = occurrences.map((o) => o.remediation).find((r): r is string => !!r) ?? null

  return (
    <Fragment>
      <tr
        className="row-link"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        // Guarded on the row itself so Space inside a nested control is not hijacked.
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault()
            onToggle()
          }
        }}
      >
        <td style={{ width: 24 }}>
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${view.what}`}
            onClick={(e) => { e.stopPropagation(); onToggle() }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: '0.65rem', padding: 0 }}
          >
            {expanded ? '\u25be' : '\u25b8'}
          </button>
        </td>
        <td><SeverityBadge severity={group.severity} /></td>
        <td style={{ fontFamily: 'var(--mono)', fontSize: '0.7rem', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
          {view.categoryLabel}
        </td>
        <td style={{ fontSize: '0.8rem' }}>{view.what}</td>
        <td style={{ fontFamily: 'var(--mono)', fontSize: '0.75rem', color: 'var(--text2)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span>
              {view.where ?? '-'}
              {group.whereVaries && <span className="dim"> +{extra}</span>}
            </span>
            {view.whereType && <span style={{ fontSize: '0.62rem', color: 'var(--text3)' }}>{view.whereType}</span>}
          </div>
        </td>
        <td>
          <DetailCell detail={view.detail} />
          {group.detailVaries && <span style={{ color: 'var(--text3)', fontSize: '0.7rem' }}> +{extra}</span>}
        </td>
        <td style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: occurrences.length > 1 ? 'var(--red)' : 'var(--text2)' }}>
          {occurrences.length}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ background: 'var(--bg3)', padding: '8px 12px 10px', borderTop: 'none' }}>
            <div style={{ display: 'grid', gap: 6 }}>
              {occurrences.map((o) => <OccurrenceLine key={o.id} f={o} />)}
              {(guide || remediation) && (
                <div style={{ marginTop: 4, paddingTop: 6, borderTop: '1px solid var(--border2)', fontSize: '0.72rem', color: 'var(--text2)' }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)', letterSpacing: '0.08em', marginRight: 8 }}>
                    HOW TO FIX
                  </span>
                  {guide ?? remediation}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}

